import {
  useGetAccountsForCareseeker,
  useLazyGetSSOIntegrationRights,
} from "apollo/hooks/queries";
import {
  PAGINATION_COUNT_20,
  PAGINATION_START_0,
  SORT_ORDER_ASC,
} from "core/consts";
import auth from "core/model/api/endpoints/auth";
import { formatUnixDate } from "core/model/utils/dates";
import { getErrorMessage } from "core/model/utils/errors";
import { getEmailDomain } from "core/model/utils/strings";
import {
  Account,
  Careseeker,
  GetAccountsForCareseekerQueryString,
  SortOrder,
} from "core/types";
import {
  CreateStepDataMap,
  useMultiStepConfirmationDialog,
} from "ds_legacy/components/ConfirmationDialog/useMultiStepConfirmationDialog";
import { dp } from "ds_legacy/materials/metrics";
import { NonIconOnlyButtonProps } from "ds/ui";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useRef, useState } from "react";
import { useLoggedInAccount, useOnLogout } from "reduxentities/hooks";
import { Locale, useTranslations } from "translations";
import { SSODialogContent, SSOHeaderIcon } from "./SSODialogContent";
import { useCreateAccountForCareseeker } from "./useCreateAccountForCareseeker";
import { useAccountByEmailHandler } from "./useGetAccountByEmail";
import { useUpdateAccountForCareseeker } from "./useUpdateAccountForCareseeker";

// Define step data types for SSO activation flow
type SSOActivationStepData = CreateStepDataMap<{
  activation: { activated: boolean };
  consent: {
    cancelButtonProps?: Omit<NonIconOnlyButtonProps, "onPress" | "ref">;
    cancelText?: string;
    confirmButtonProps?: Omit<NonIconOnlyButtonProps, "onPress" | "ref">;
    confirmed: boolean;
    content?: React.ReactNode;
    submitText?: string;
    title?: string;
  };
}>;

export type AccountDialogState = {
  account?: Account;
  error?: string;
  isOnlyActiveUserManager?: boolean;
  isOtherCareseekerAccount?: boolean;
  prevStep?: Step | null;
  step: Step | null;
};

export type GetAccountsForCareseekerFilters = {
  count: number;
  search_term: string;
  sort_field: string;
  sort_order: SortOrder;
  start: number;
};

type Step = (typeof _STEPS)[number];

export const STEP_1 = 1;
export const STEP_2 = 2;
const _STEPS = [STEP_1, STEP_2] as const;

// don't change this unless you change it in the BE too
export const SSO_ACTIVATION_SUCCESS_PARAM = "isActivation";

export function buildQueryString(
  filters: GetAccountsForCareseekerFilters,
): GetAccountsForCareseekerQueryString {
  return `search_term=${encodeURIComponent(filters.search_term)}&start=${
    filters.start
  }&count=${filters.count}&sort_field=${filters.sort_field}&sort_order=${
    filters.sort_order
  }`;
}

export function getTimestamp(locale: Locale) {
  return encodeURIComponent(
    formatUnixDate(new Date().getTime() / 1000, locale, {
      customFormat: "dd.MM.yyyy HH:mm",
    }),
  );
}
export const initialPagination = {
  count: PAGINATION_COUNT_20,
  start: PAGINATION_START_0,
} as const;

export function useUserManagementFeature({
  careseeker,
}: {
  careseeker: Careseeker | undefined;
}) {
  const loggedAccount = useLoggedInAccount();
  const translations = useTranslations();
  const toast = useToast();
  const [filtersState, setFiltersState] =
    useState<GetAccountsForCareseekerFilters>({
      search_term: "",
      ...initialPagination,
      sort_field: "status",
      sort_order: SORT_ORDER_ASC,
    });
  const filters = buildQueryString(filtersState);

  const [accountDialog, setAccountDialog] = useState<AccountDialogState>({
    account: undefined,
    step: null,
  });

  const [accountsProgress, accountsPayload] = useGetAccountsForCareseeker({
    fetchPolicy: "network-only",
    variables: { careseekerId: careseeker?.id || -1, filters },
  });

  const getAccountProps = useAccountByEmailHandler({
    careseeker,
    setAccountDialog,
  });

  const createAccountProps = useCreateAccountForCareseeker({
    careseeker,
    setAccountDialog,
  });

  const updateAccountProps = useUpdateAccountForCareseeker({
    careseeker,
    setAccountDialog,
  });

  const [fetchSSOIntegrationRights] = useLazyGetSSOIntegrationRights();

  const {
    confirmMultiStep,
    props: ssoMultiStepProps,
    setLoading,
    updateDialogProps,
  } = useMultiStepConfirmationDialog<SSOActivationStepData>();

  const onLogout = useOnLogout();

  // Careseeker ids the user manager selected to activate SSO for. Stored in a
  // ref so the activation step reads the latest value without rebuilding the
  // dialog.
  const selectedCareseekerIdsRef = useRef<number[]>([]);

  const handleActivateSSO = async () => {
    await confirmMultiStep([
      {
        cancelText: translations.userManagement.ssoFlow.enableLaterButton,
        confirmButtonProps: { id: "sso-consent" },
        content: (
          <p className="text-gray-600">
            {translations.userManagement.ssoFlow.consentInformation}
          </p>
        ),
        headerIcon: <SSOHeaderIcon variant="info" />,
        id: "consent",
        onStepSubmit: async (_data?: SSOActivationStepData) => {
          setLoading(true);

          try {
            const domain = getEmailDomain(loggedAccount?.email);
            if (!domain) {
              throw new Error(
                "Unable to extract email domain for SSO activation. Please ensure you have a valid email address.",
              );
            }

            const ssoIntegrationsRight = await fetchSSOIntegrationRights({
              domain,
            });

            if (!ssoIntegrationsRight || ssoIntegrationsRight.length === 0) {
              throw new Error(
                "No SSO integration rights found for this domain.",
              );
            }

            const stepTwo = translations.userManagement.ssoFlow.stepTwo;
            const hasRightsForAll = ssoIntegrationsRight.every(
              (integration) => integration.has_admin_rights,
            );
            // Senders the user manages that still need activating (not already
            // consented) — these are the ones selectable in the multi-select.
            const hasSendersToActivate = ssoIntegrationsRight.some(
              (integration) =>
                integration.has_admin_rights && !integration.with_consent,
            );

            // The user manager picks which senders to activate; the submit is
            // hidden only when there is nothing left to activate.
            const canProceed = hasSendersToActivate;

            selectedCareseekerIdsRef.current = [];

            return {
              ...(canProceed
                ? {}
                : {
                    cancelButtonProps: {
                      color: "primary",
                      id: "sso-understood",
                      variant: "solid",
                    },
                    cancelText: stepTwo.understandButton,
                    submitText: null, // null to hide the button
                  }),
              content: (
                <SSODialogContent
                  headerNotUserManager={
                    hasRightsForAll ? undefined : stepTwo.headerNotUserManager
                  }
                  headerUserManager={stepTwo.headerUserManagerRights}
                  integrations={ssoIntegrationsRight}
                  isSelectable
                  showContactSupport={!hasRightsForAll}
                  subtitle={stepTwo.selectionSubtitle}
                  onSelectionChange={(ids) => {
                    selectedCareseekerIdsRef.current = ids;
                    // Keep the activation submit disabled until at least one
                    // sender is selected. Only ever set isDisabled to true —
                    // passing false would override the dialog's own
                    // loading-disable and allow double submit.
                    updateDialogProps({
                      confirmButtonProps:
                        ids.length === 0
                          ? { id: "sso-activate", isDisabled: true }
                          : { id: "sso-activate" },
                    });
                  }}
                />
              ),
              title: stepTwo.selectionTitle,
            };
          } catch (error) {
            toast({ color: "danger", message: translations.general.error });
            console.error("SSO activation failed:", getErrorMessage(error));
          } finally {
            // Clear loading state
            setLoading(false);
          }
        },
        submitText: translations.userManagement.ssoFlow.consentButton,
        sx: { maxWidth: dp(425) },
        title: translations.userManagement.ssoFlow.consentModalHeader,
      },
      {
        cancelText: translations.actions.cancel,
        // The submit starts disabled and is enabled once a sender is selected
        // (see onSelectionChange above).
        confirmButtonProps: { id: "sso-activate", isDisabled: true },
        headerIcon: <SSOHeaderIcon variant="info" />,
        id: "activation",
        onStepSubmit: async (_data?: SSOActivationStepData) => {
          // Submit is disabled until a sender is selected; guard defensively.
          if (selectedCareseekerIdsRef.current.length === 0) {
            return false;
          }

          setLoading(true);
          try {
            if (!loggedAccount?.email) {
              throw new Error("No email address available for SSO activation");
            }

            const redirectBase = new URL(window.location.href);
            redirectBase.searchParams.set(SSO_ACTIVATION_SUCCESS_PARAM, "true");

            const res = await auth.oauthCheck({
              careseeker_ids: selectedCareseekerIdsRef.current,
              email: loggedAccount.email,
              is_user_manager: true,
              redirect_to_url: btoa(redirectBase.toString()),
            });

            const ssoUrl = res?.url;
            if (ssoUrl) {
              // Logout current user before redirecting to SSO
              await onLogout();

              window.location.href = ssoUrl;
              return true;
            } else {
              throw new Error(
                "This email is not eligible for SSO authentication",
              );
            }
          } catch (ssoError) {
            toast({
              color: "danger",
              message: translations.general.error,
            });
            console.error("SSO activation failed:", getErrorMessage(ssoError));
            return false;
          }
        },
        submitText: translations.userManagement.ssoActivationButton,
        sx: { maxWidth: dp(425) },
      },
    ]);
  };

  return {
    ...accountsPayload,
    ...createAccountProps,
    ...getAccountProps,
    ...updateAccountProps,
    accountDialog,
    accountsProgress,
    filtersState,
    handleActivateSSO,
    setAccountDialog,
    setFiltersState,
    ssoMultiStepProps,
  };
}
